/* eslint-disable @typescript-eslint/no-use-before-define */
import type { Item as DialItem } from '@studio/design-system/dial';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
//import { hasMovement } from '@studio/models/ptz';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import {
  DetectionMode,
  FrameAlign,
  FrameZoom,
  ShotSafetyFallback
} from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { AutoTrackingState, DeadBand } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import { useState } from 'react';
import { DEADBAND_MARGIN_X, DEADBAND_MARGIN_Y, deadbandDimensions } from './deadband-dimensions.js';
import { locateProxy } from '#~/agent/index.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { moveTo } from '#~/control/movement/commands/index.js';
import { usePiston } from '#~/control/movement/hooks.js';
import type { Piston } from '#~/control/movement/index.js';
import type { DeviceProps } from '#~/device/components/index.js';
import { logger } from '#~/logger/index.js';
import { pbToJsPtz } from '#~/protobuf/index.js';
import { toHexFromId } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { setTrackerCPUMode } from '#~/control/components/interaction/autotracking/wiring.js';

//const PanRateDigitalAdjustment = 5;
//const TiltRateDigitalAdjustment = 5;

let deadbandChangeTimeout: NodeJS.Timeout | undefined;

/** @internal */
export type Wiring = Readonly<{
  autoTrackingState: T_Maybe<AutoTrackingState>;
  deadbandHeight: number;
  deadbandSafetyArea: T_Maybe<boolean>;
  deadbandWidth: number;
  determineSelectedOptionIndex: (currentValue: number) => T_Maybe<DialItem>;
  frameAlign: T_Maybe<FrameAlign>;
  frameZoom: T_Maybe<FrameZoom>;
  handleChangeDeadbandHeight: (height: number) => void;
  handleChangeDeadbandWidth: (width: number) => void;
  handleChangeFrameAlign: (align: FrameAlign) => void;
  handleChangeFrameZoom: (zoom: FrameZoom) => void;
  handleChangeShotSafetyMode: (shotSafetyMode: string) => void;
  handleChangeTrackingSpeed: (trackingSpeed: number) => void;
  handleChangeZoomManual: (item: DialItem, _: number) => void;
  handleCommitDeadbandChange: () => void;
  handleSetSafePosition: () => void;
  handleToggleDeadbandSafetyArea: () => void;
  handleToggleShotProtection: () => void;
  handleToggleTrackingSpeedAdaptive: () => void;
  handleToggleZoomAdaptive: () => void;
  handleUnsetSafePosition: () => void;
  handleViewSafePosition: () => void;
  handleToggleCPUMode: () => void;
  isCompositionActive: boolean;
  isCompositionEnabled: boolean;
  isLoaded: boolean;
  piston: Piston;
  safePosition: T_Maybe<DeepPartial<T_Optional<PTZ>>>;
  setTrackingSpeedCustom: (trackingSpeedCustom: boolean) => void;
  shotProtection: T_Maybe<boolean>;
  shotSafetyFallback: T_Maybe<ShotSafetyFallback>;
  trackingSpeed: T_Maybe<number>;
  trackingSpeedAdaptive: T_Maybe<boolean>;
  trackingSpeedCustom: boolean;
  trackingSpeedMap: {
    fast: number;
    medium: number;
    slow: number;
  };
  zoomAdaptive: T_Maybe<boolean>;
  zoomManual: T_Maybe<number>;
  zoomManualOptionItems: T_Maybe<DialItem[]>;
  isLowCPUModeEnabled: boolean;
}>;

/** @internal */
export const useWiring = ({
  deviceId
}: DeviceProps & {
  /** Agent ID. */ agentId: Agent['id'];
}): Wiring => {
  const proxy = locateProxy(deviceId);
  const { getAccessTokenSilently } = useAuth0();

  const workerId = Object.values(proxy?.state?.control?.studio?.autoTrackingReservation ?? []).find(
    (r) => toHexFromId(r?.cameraId) === deviceId
  )?.workerId;
  const workerIdHex = toHexFromId(workerId) ?? '';
  const autoTrackingState = proxy?.state?.control?.studio?.autoTrackingWorker?.[workerIdHex]?.state;

  const state = autoTrackingState?.control ?? undefined;

  const mutateAutoTrackingState = async (
    set: DeepPartial<AutoTrackingState>,
    unset: DeepPartial<AutoTrackingState>
  ): Promise<void> => {
    await proxy?.mutate(
      Object.entries(set).length > 0
        ? {
            control: {
              studio: {
                autoTrackingWorker: {
                  [workerIdHex]: {
                    state: set
                  }
                }
              }
            }
          }
        : {},
      Object.entries(unset).length > 0
        ? {
            control: {
              studio: {
                autoTrackingWorker: {
                  [workerIdHex]: {
                    state: unset
                  }
                }
              }
            }
          }
        : {},
      true
    );
  };

  const [trackingSpeedCustom, setTrackingSpeedCustom] = useState(false);

  const [transientTrackingSpeed, setTransientTrackingSpeed] = useState<number | undefined>(
    undefined
  );

  const [trackingSpeedChangeTimeout, setTrackingSpeedChangeTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  const handleManualAdjustment = useLatestFn((_ptz: PTZ): void => {
    /*
    if (!hasMovement(ptz)) {
      return;
    }

    const { pan, tilt, zoom } = ptz;

    const current = autoTrackingState?.state.current.message.control?.manualAdjustment;

    const next = {
      pan: (current?.pan ?? 0) + pan * PanRateDigitalAdjustment,
      tilt: (current?.tilt ?? 0) - tilt * TiltRateDigitalAdjustment,
      zoom: (current?.zoom ?? 0) - zoom
    };

    autoTrackingState
      ?.requestMutation(
        {
          control: {
            manualAdjustment: next
          }
        },
        {}
      )
      .catch(noop);
      */
  });

  const piston = usePiston(<Device['id']>`${deviceId}.autotracking`, handleManualAdjustment);

  const handlePersistAutoTrackingState = async (
    snapshot: T_Maybe<AutoTrackingState>
  ): Promise<void> => {
    if (!snapshot || !proxy) {
      return;
    }

    const token = await getAccessTokenSilently();

    proxy
      .mutate(
        {
          control: {
            studio: {
              device: {
                [deviceId]: {
                  autoTrackingControl: {
                    safePosition: {
                      pan: snapshot.control?.safePosition?.pan,
                      tilt: snapshot.control?.safePosition?.tilt,
                      zoom: snapshot.control?.safePosition?.zoom
                    },
                    shotSafetyFallback:
                      snapshot.control?.shotSafetyFallback ??
                      ShotSafetyFallback.SHOT_SAFETY_FALLBACK_UNKNOWN,
                    trackingSpeed: snapshot.control?.trackingSpeed,
                    zoomAdaptive: snapshot.control?.zoomAdaptive,
                    zoomManual: snapshot.control?.zoomManual
                  }
                }
              }
            }
          }
        },
        {}
      )
      .then(async () => proxy.save(token))
      .catch((error) => {
        logger.error(error);
      });
  };

  /* eslint-enable hooks/sort */
  const handleToggleDeadbandSafetyArea = (): void => {
    /*
    autoTrackingState
      ?.requestMutation(
        {
          control: {
            deadbandSafetyArea: !state?.deadBandSafetyArea
          }
        },
        {}
      )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
      */
  };

  const handleToggleShotProtection = (): void => {
    /*
    autoTrackingState
      ?.requestMutation(
        {
          control: {
            shotProtection: !state?.shotProtection
          }
        },
        {}
      )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
      */
  };

  const handleToggleZoomAdaptive = (): void => {
    mutateAutoTrackingState(
      {
        control: {
          zoomAdaptive: !state?.zoomAdaptive
        }
      },
      {}
    )
      .catch(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleToggleTrackingSpeedAdaptive = (): void => {
    /*
    autoTrackingState
      ?.requestMutation(
        {
          control: {
            trackingSpeedAdaptive: !state?.trackingSpeedAdaptive
          }
        },
        {}
      )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
      */
  };

  const handleChangeShotSafetyMode = (position: string): void => {
    const value = Number.parseInt(position, 10);

    mutateAutoTrackingState(
      {
        control: {
          shotSafetyFallback: value
        }
      },
      {}
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleChangeFrameAlign = (unsafe_value: FrameAlign): void => {
    const value = unsafe_value;

    let deadBand: DeepPartial<DeadBand> = {
      hmc: 0.5,
      vmc: 0.35,
      lht: 0.1,
      rht: 0.1,
      tvt: 0.15,
      bvt: 0.15,
      hr: 0.05
    };

    if (value === FrameAlign.FRAME_ALIGN_LEFT) {
      deadBand = {
        ...deadBand,
        hmc: 0.15,
        lht: 0.05
      };
    }

    if (value === FrameAlign.FRAME_ALIGN_RIGHT) {
      deadBand = {
        ...deadBand,
        hmc: 0.85,
        rht: 0.05
      };
    }

    mutateAutoTrackingState(
      {
        control: {
          deadBand,
          frameAlign: value
        }
      },
      {}
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleChangeFrameZoom = (unsafe_value: FrameZoom): void => {
    let value = unsafe_value;

    if (value === state?.frameZoom) {
      value = FrameZoom.FRAME_ZOOM_NONE;
    }

    mutateAutoTrackingState(
      {
        control: {
          frameZoom: value
        }
      },
      {}
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleChangeTrackingSpeed = (speed: number): void => {
    setTransientTrackingSpeed(speed);

    clearTimeout(trackingSpeedChangeTimeout);

    setTrackingSpeedChangeTimeout(
      setTimeout(() => {
        setTrackingSpeedChangeTimeout(undefined);

        mutateAutoTrackingState(
          {
            control: {
              trackingSpeed: speed
            }
          },
          {}
        )
          .then(() => {
            handlePersistAutoTrackingState(<AutoTrackingState>{
              ...autoTrackingState,
              control: {
                ...autoTrackingState?.control,
                trackingSpeed: speed
              }
            });
          })
          .catch(noop)
          .finally(() => {
            setTimeout(() => {
              setTransientTrackingSpeed(undefined);
            }, 500);
          });
      }, 500)
    );
  };

  const zoomManualOptionItems: DialItem[] = [
    {
      label: '0.0',
      value: 0
    },
    {
      label: '0.25',
      value: 0.25
    },
    {
      label: '0.50',
      value: 0.5
    },
    {
      label: '0.75',
      value: 0.75
    },
    {
      label: '1.0',
      value: 1
    }
  ];

  const handleChangeZoomManual = (item: DialItem, _: number): void => {
    const value = Number.parseFloat(<string>(typeof item === 'object' ? item.value : item));

    mutateAutoTrackingState(
      {
        control: {
          zoomManual: value
        }
      },
      {}
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const determineSelectedOptionIndex = (currentValue: number = -1): T_Maybe<DialItem> =>
    zoomManualOptionItems[
      zoomManualOptionItems
        .map((item, index) => ({
          delta: Math.abs(
            Number.parseFloat(<string>(typeof item === 'object' ? item.value : item)) - currentValue
          ),
          index
        }))
        .sort((a, b) => a.delta - b.delta)[0]?.index ?? 0
    ];

  const handleSetSafePosition = (): void => {
    const cameraStatus = proxy?.state.status?.device[deviceId];

    const position = pbToJsPtz(cameraStatus?.movement?.position);

    mutateAutoTrackingState(
      {
        control: {
          safePosition: {
            pan: position.pan,
            tilt: position.tilt,
            zoom: position.zoom
          }
        }
      },
      {}
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleUnsetSafePosition = (): void => {
    mutateAutoTrackingState(
      {},
      {
        control: {
          safePosition: {
            pan: 0,
            tilt: 0,
            zoom: 0
          }
        }
      }
    )
      .then(() => {
        handlePersistAutoTrackingState(autoTrackingState);
      })
      .catch(noop);
  };

  const handleViewSafePositionNoGuard = (): void => {
    const safePosition = autoTrackingState?.control?.safePosition;

    if (
      safePosition?.pan !== undefined &&
      safePosition.tilt !== undefined &&
      safePosition.zoom !== undefined
    ) {
      moveTo(
        deviceId,
        {
          pan: safePosition.pan,
          tilt: safePosition.tilt,
          zoom: safePosition.zoom
        },
        false
      ).catch(noop);
    }
  };

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const handleViewSafePosition = (): void => {
    if (isProtected) {
      triggerControlAlert?.(() => {
        handleViewSafePositionNoGuard();
      });

      return;
    }

    handleViewSafePositionNoGuard();
  };

  const trackingSpeedMap = {
    fast: 1.0,
    medium: 0.5,
    slow: 0.25
  };

  const trackingSpeed =
    transientTrackingSpeed !== undefined
      ? transientTrackingSpeed
      : (state?.trackingSpeed ?? undefined);

  const isTracking =
    autoTrackingState?.control?.detectionMode === DetectionMode.DETECTION_MODE_TRACKING;

  const frameZoom = autoTrackingState?.control?.frameZoom ?? FrameZoom.FRAME_ZOOM_NONE;

  const [produceDeadband, realTimeDeadbandDimensions] = deadbandDimensions((s) => [
    s.produce,
    s.deadbandDimensions
  ]);

  const deadbandWidth = realTimeDeadbandDimensions.width;
  const deadbandHeight = realTimeDeadbandDimensions.height;

  const scheduleDeadbandChange = (): void => {
    clearTimeout(deadbandChangeTimeout);

    deadbandChangeTimeout = setTimeout(() => {
      handleCommitDeadbandChange();
    }, 500);
  };

  const handleChangeDeadbandWidth = (width: number): void => {
    produceDeadband((draft) => {
      draft.deadbandOverrides.width = Math.min(Math.max(width, 0.05), 1 - DEADBAND_MARGIN_X * 2);
      draft.deadbandDimensions.width = Math.min(Math.max(width, 0.05), 1 - DEADBAND_MARGIN_X * 2);
    });

    scheduleDeadbandChange();
  };

  const handleChangeDeadbandHeight = (height: number): void => {
    produceDeadband((draft) => {
      draft.deadbandOverrides.height = Math.min(Math.max(height, 0.05), 1 - DEADBAND_MARGIN_Y * 2);
      draft.deadbandDimensions.height = Math.min(Math.max(height, 0.05), 1 - DEADBAND_MARGIN_Y * 2);
    });

    scheduleDeadbandChange();
  };

  const handleCommitDeadbandChange = (): void => {
    produceDeadband((draft) => {
      draft.deadbandOverrides.commit = true;
    });
  };

  // ####### Auto-Tracking (older-go) CPU Mode #######
  const [isLowCPUModeEnabled, setIsLowCPUModeEnabled] = useState(true);

  const handleToggleCPUMode = useLatestFn(() => {
    setIsLowCPUModeEnabled(!isLowCPUModeEnabled);

    setTrackerCPUMode(!isLowCPUModeEnabled ? 'yolo' : 'efftam');
  });

  return {
    autoTrackingState: autoTrackingState,
    deadbandSafetyArea: false, //state?.deadBandSafetyArea,
    determineSelectedOptionIndex,
    frameAlign: state?.frameAlign,
    frameZoom: state?.frameZoom,
    handleChangeFrameAlign,
    handleChangeFrameZoom,
    handleChangeShotSafetyMode,
    handleChangeTrackingSpeed,
    handleChangeZoomManual,
    handleSetSafePosition,
    handleCommitDeadbandChange,
    handleToggleDeadbandSafetyArea,
    handleToggleShotProtection,
    handleToggleTrackingSpeedAdaptive,
    handleToggleZoomAdaptive,
    handleUnsetSafePosition,
    handleViewSafePosition,
    handleToggleCPUMode,
    isCompositionActive: frameZoom !== FrameZoom.FRAME_ZOOM_NONE,
    isCompositionEnabled: !!isTracking,
    isLoaded: !!state,
    piston,
    safePosition: state?.safePosition,
    setTrackingSpeedCustom,
    shotProtection: false, //state?.shotProtection,
    shotSafetyFallback: state?.shotSafetyFallback,
    trackingSpeed,
    trackingSpeedAdaptive: false, //state?.trackingSpeedAdaptive,
    trackingSpeedCustom,
    trackingSpeedMap,
    zoomAdaptive: state?.zoomAdaptive,
    zoomManual: state?.zoomManual,
    zoomManualOptionItems,
    deadbandWidth,
    deadbandHeight,
    isLowCPUModeEnabled,
    handleChangeDeadbandWidth,
    handleChangeDeadbandHeight
  };
};
